import { useState } from "react";
import {
  Control,
  Controller,
  FieldErrors,
  FieldValues,
  Path,
  UseFormSetValue,
} from "react-hook-form";
import { PlusIcon, UserGroupIcon, XCircleIcon } from "@heroicons/react/24/outline";
import {
  PublicUserOrganization,
  OrganizationType,
  UserGroupFragment,
} from "../../../../../../../../../../__generated__/graphql";
import ParticipantsSelectionModal from "../../../../../../../../../../components/ParticipantsSelectionModal";
import SelectedMembersDisplay from "../../../../../../../../../../components/SelectedMembersDisplay";
import Button from "../../../../../../../../../../components/Button";

export interface ParticipantOption {
  value: string;
  organizationId: string;
  userOrganizationId: string;
  label: string;
}

export interface GroupParticipantOption {
  id: string;
  name: string;
}

interface ParticipantsSelectInputProps<T extends FieldValues> {
  control: Control<T>;
  errors: FieldErrors<T>;
  participantOptions: ParticipantOption[];
  placeholder?: string;
  label?: string;
  required?: boolean;
  onChange?: (participants: ParticipantOption[]) => void;
  onGroupsChange?: (groups: GroupParticipantOption[]) => void;
  showGroups?: boolean;
  projectId?: string;
  setValue?: UseFormSetValue<T>;
  currentGroups?: GroupParticipantOption[];
}

// Convert ParticipantOption to PublicUserOrganization
// Note: organizationName and organizationType will be incomplete, but ParticipantsSelectionModal will provide full data
const participantOptionToPublicUserOrganization = (
  option: ParticipantOption
): PublicUserOrganization => {
  return {
    id: option.userOrganizationId,
    name: option.label,
    organizationId: option.organizationId,
    organizationName: "", // Will be filled by ParticipantsSelectionModal
    organizationType: OrganizationType.Other, // Default, will be replaced by modal data
    email: null,
    phoneNumber: null,
    avatarBucketKey: null,
    jobTitle: null,
    birthDate: null,
  };
};

// Convert PublicUserOrganization to ParticipantOption
const publicUserOrganizationToParticipantOption = (
  user: PublicUserOrganization,
  participantOptions: ParticipantOption[]
): ParticipantOption | null => {
  // Find the matching option to preserve all fields
  const matchingOption = participantOptions.find(
    (opt) => opt.userOrganizationId === user.id
  );
  if (matchingOption) {
    return matchingOption;
  }
  // Fallback: create from PublicUserOrganization
  return {
    value: user.id,
    organizationId: user.organizationId,
    userOrganizationId: user.id,
    label: user.name,
  };
};

// This component is used to select participants within a form
export default function ParticipantsSelectInput<T extends FieldValues>({
  control,
  errors,
  participantOptions,
  label = "Participants",
  placeholder = "Sélectionner un ou plusieurs participants",
  required = true,
  onChange,
  onGroupsChange,
  showGroups = false,
  projectId,
  setValue,
  currentGroups = [],
}: ParticipantsSelectInputProps<T>) {
  const [isSelectingParticipants, setIsSelectingParticipants] = useState(false);

  // Convert UserGroupFragment to GroupParticipantOption
  const userGroupToGroupOption = (group: UserGroupFragment): GroupParticipantOption => ({
    id: group.id,
    name: group.name,
  });

  // Convert GroupParticipantOption to UserGroupFragment (partial)
  const groupOptionToUserGroup = (option: GroupParticipantOption): UserGroupFragment => ({
    id: option.id,
    name: option.name,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    organizationId: "",
    createdByUserOrganizationId: "",
    createdByPublicUserOrganization: {
      id: "",
      name: "",
      organizationId: "",
      organizationName: "",
      organizationType: OrganizationType.Other,
      email: null,
      phoneNumber: null,
      avatarBucketKey: null,
      jobTitle: null,
      birthDate: null,
    },
    projectId: null,
    eventId: null,
    description: null,
    members: [],
  });

  return (
    <Controller
      name={"participants" as Path<T>}
      control={control}
      rules={{
        required: required
          ? "Veuillez sélectionner au moins un participant"
          : false,
      }}
      render={({ field }) => {
        const selectedParticipants = (field.value as ParticipantOption[]) || [];
        const selectedMembers: PublicUserOrganization[] = selectedParticipants
          .map(participantOptionToPublicUserOrganization);

        const handleMembersAndGroupsSelected = (
          members: PublicUserOrganization[],
          groups?: UserGroupFragment[]
        ) => {
          const newParticipants = members
            .map((member) =>
              publicUserOrganizationToParticipantOption(member, participantOptions)
            )
            .filter(
              (opt): opt is ParticipantOption => opt !== null
            );
          field.onChange(newParticipants);
          onChange?.(newParticipants);

          // Handle groups
          if (showGroups && groups && setValue) {
            const newGroups = groups.map(userGroupToGroupOption);
            setValue("groupParticipants" as Path<T>, newGroups as never);
            onGroupsChange?.(newGroups);
          }
        };

        const removeMember = (memberId: string) => {
          const newParticipants = selectedParticipants.filter(
            (p) => p.userOrganizationId !== memberId
          );
          field.onChange(newParticipants);
          onChange?.(newParticipants);
        };

        const removeGroup = (groupId: string) => {
          if (setValue) {
            const newGroups = currentGroups.filter((g) => g.id !== groupId);
            setValue("groupParticipants" as Path<T>, newGroups as never);
            onGroupsChange?.(newGroups);
          }
        };

        const hasContent = selectedMembers.length > 0 || currentGroups.length > 0;

        return (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label htmlFor="participants" className="block text-sm font-medium">
                {label}
              </label>
              <Button
                type="button"
                variant="secondary"
                className="gap-1 text-sm"
                onClick={() => setIsSelectingParticipants(true)}
              >
                <PlusIcon className="w-4 h-4" />
                {hasContent
                  ? showGroups
                    ? "Modifier"
                    : "Modifier les participants"
                  : showGroups
                  ? "Ajouter des participants ou groupes"
                  : "Ajouter des participants"}
              </Button>
            </div>

            <ParticipantsSelectionModal
              isOpen={isSelectingParticipants}
              onClose={() => setIsSelectingParticipants(false)}
              onSubmit={handleMembersAndGroupsSelected}
              title={showGroups ? "Sélectionner les participants et groupes" : "Sélectionner les participants"}
              description={showGroups ? "Choisissez les participants et groupes pour cette activité." : "Choisissez les participants pour cette activité."}
              initialSelectedUsers={selectedMembers}
              initialSelectedGroups={currentGroups.map(groupOptionToUserGroup)}
              showGroups={showGroups}
              projectId={projectId}
              submitButtonText="Valider la sélection"
            />

            {!hasContent ? (
              <div
                className="border-2 border-dashed border-border rounded-lg p-6 flex flex-col items-center justify-center gap-2 cursor-pointer hover:border-accent/50 hover:bg-accent/5 transition-colors"
                onClick={() => setIsSelectingParticipants(true)}
              >
                <UserGroupIcon className="w-8 h-8 text-tertiary" />
                <div className="text-sm text-tertiary text-center">
                  {placeholder}
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {/* Display groups */}
                {currentGroups.length > 0 && (
                  <div>
                    <div className="text-xs font-medium text-secondary uppercase tracking-wider mb-2">
                      Groupes ({currentGroups.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {currentGroups.map((group) => (
                        <div
                          key={group.id}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-accent/10 border border-accent/30 rounded-full text-sm group"
                        >
                          <UserGroupIcon className="w-4 h-4 text-accent" />
                          <span className="text-primary font-medium">{group.name}</span>
                          <button
                            type="button"
                            onClick={() => removeGroup(group.id)}
                            className="opacity-0 group-hover:opacity-100 hover:text-red-500 transition-all ml-1"
                          >
                            <XCircleIcon className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Display individual participants */}
                {selectedMembers.length > 0 && (
                  <div>
                    {currentGroups.length > 0 && (
                      <div className="text-xs font-medium text-secondary uppercase tracking-wider mb-2">
                        Personnes ({selectedMembers.length})
                      </div>
                    )}
                    <SelectedMembersDisplay
                      members={selectedMembers}
                      onRemove={removeMember}
                    />
                  </div>
                )}
              </div>
            )}

            {errors.participants && (
              <p className="text-red-500 text-sm mt-1">
                {errors.participants.message?.toString()}
              </p>
            )}
          </div>
        );
      }}
    />
  );
}
